import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SchedulerService } from '../service/scheduler.service';
import { Router, ActivatedRoute, RouterEvent, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { LoginService, DataReloadEvent, TableComponent, TableColumn, DateTableColumn, TableElement, NativeStateComponent } from '@serengeti/serengeti-common';
import { SchedulerRuleService } from '../service/scheduler.rule.service';
import { SchedulerHistoryService } from '../service/scheduler.history.service';

import {default as _rollupMoment, Moment} from 'moment';
import { NgForm } from '@angular/forms';

@Component({
	selector: 'app-service-scheduler-detail',
	templateUrl: './detail.component.html',
	styleUrls: ['./detail.component.scss']
})
export class SchedulerDetailComponent implements OnInit, OnDestroy {

	id: string;
	scheduler = {
		name: null,
		state: "Active",
		description: null,
		rules: [],
		tasks: []
	};

	selectedRule = {
		name: '',
		description: '',
		viewSelected: false
	};
	rules = [];
	resultPage: any;

	routeSubscription: Subscription;

	selectedIndex = 0;

	queryParamSubscription: Subscription;
	selectedTaskHistoryId: string;
	selectedTaskHistory: any;

	@ViewChild('searchTaskHistory', { static: false }) input: ElementRef;
	searchTaskHistoryName: string;

	selectedFromDateText: string;
	_selectedFromDate: Date;
	set selectedFromDate(_date: Date) {
		this._selectedFromDate = _date;
		if (_date && _date.toUTCString) {
			this.translateService.get('service.scheduler.detail.tabs.history.list.content.format-from-date', { year: _date.getFullYear(), month: _date.getMonth() + 1, day: _date.getDate() })
			.subscribe((message: string) => {
				this.selectedFromDateText = message;
			});
		} else {
			const date = _date as unknown as Moment;

			this.translateService.get('service.scheduler.detail.tabs.history.list.content.format-from-date', { year: date.year(), month: date.month() + 1, day: date.date() })
			.subscribe((message: string) => {
				this.selectedFromDateText = message;
			});
		}
	}
	get selectedFromDate(): Date {
		return this._selectedFromDate;
	}

	selectedToDateText: string;
	_selectedToDate: Date;
	set selectedToDate(_date: Date) {
		this._selectedToDate = _date;
		if (_date && _date.toUTCString) {
			this.translateService.get('service.scheduler.detail.tabs.history.list.content.format-to-date', { year: _date.getFullYear(), month: _date.getMonth() + 1, day: _date.getDate() })
			.subscribe((message: string) => {
				this.selectedToDateText = message;
			});  
		} else {
			const date = _date as unknown as Moment;

			this.translateService.get('service.scheduler.detail.tabs.history.list.content.format-to-date', { year: date.year(), month: date.month() + 1, day: date.date() })
			.subscribe((message: string) => {
				this.selectedToDateText = message;
			});
		}
	}
	get selectedToDate(): Date {
		return this._selectedToDate;
	}

	currentCondition: DataReloadEvent;
	columns: TableColumn[];

	@ViewChild('taskHistoryList', {static: false}) taskHistoryList: TableComponent;

	@ViewChild('scheduleForm', {static: true}) private scheduleForm: NgForm;

	_searchTaskHistory: ElementRef;
	@ViewChild('searchTaskHistory', { static: false }) set searchTaskHistory(_searchTaskHistory: ElementRef) {
		this._searchTaskHistory = _searchTaskHistory;
		if (_searchTaskHistory) {
			_searchTaskHistory.nativeElement.addEventListener('keypress', (e: KeyboardEvent) => {
				if (e.key === "Enter") {
					this.doSearchHistory();
				}
		 	});
		}
	}
	get searchTaskHistory(): ElementRef {
		return this._searchTaskHistory;
	}
	
	oldSearch: string;

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private translateService: TranslateService,
		private schedulerService: SchedulerService,
		private ruleService: SchedulerRuleService,
		private taskHistoryService: SchedulerHistoryService,
		private loginService: LoginService
	) {
		this.columns = [
			new TableColumn('name', 'service.scheduler.detail.tabs.history.list.content.columns.name'),
			new TableColumn('apiUrl', 'service.scheduler.detail.tabs.history.list.content.columns.api-url')
			.withStyles("api-url"),
			new TableColumn('apiMethod', 'service.scheduler.detail.tabs.history.list.content.columns.api-method'),
			new TableColumn('status', 'service.scheduler.detail.tabs.history.list.content.columns.status')
			.withStyles('cellCenter')
			.withType(NativeStateComponent)
			.withDecorator((self: TableColumn, element: TableElement) => {
				const status = element.getData().status;
	
				if (status) {
					return "service.scheduler.detail.tabs.history.list.status." + status.toLowerCase();
				} else {
					return "service.scheduler.detail.tabs.history.list.status.none";
				}
			}),
			new TableColumn('result', 'service.scheduler.detail.tabs.history.list.content.columns.result')
			.withStyles('cellCenter')
			.withType(NativeStateComponent)
			.withDecorator((self: TableColumn, element: TableElement) => {
				const resultState = element.getData().result;
	
				if (resultState) {
					return "service.scheduler.detail.tabs.history.list.result." + resultState.toLowerCase();
				} else {
					return "service.scheduler.detail.tabs.history.list.result.none";
				}
			}),
			new TableColumn('resultCode', 'service.scheduler.detail.tabs.history.list.content.columns.result-code')
			.withStyles('cellCenter')
			.withDecorator((self: TableColumn, element: TableElement) => {
				const resultCode = element.getData().resultCode;
	
				if (!resultCode) {
					return '-';
				} else {
					return resultCode;
				}
			}),
			new DateTableColumn('executedTime', 'service.scheduler.detail.tabs.history.list.content.columns.executed-time')
			.withStyles('cellCenter'),
			new DateTableColumn('completedTime', 'service.scheduler.detail.tabs.history.list.content.columns.completed-time')
			.withStyles('cellCenter'),
			new TableColumn('durationTime', 'service.scheduler.detail.tabs.history.list.content.columns.duration-time')
			.withStyles("cellRight")
			.withDecorator((_column: TableColumn, _element: TableElement) => {
				const elapsedTime = TableColumn._getData(_element.getData(), 'durationTime');

				if (elapsedTime) {
					return elapsedTime.toLocaleString();
				} else {
					return '-';
				}
			})
		];

		this.routeSubscription = this.router.events.pipe(
			filter((event: RouterEvent) => event instanceof NavigationEnd)
		).subscribe(() => {
			this.id = this.route.snapshot.paramMap.get('groupId');
			if (this.id) {
				this.schedulerService.get(this.id).subscribe((scheduler: any) => {
					this.scheduler = scheduler;
					this.goPage(0);
				});  
			} else {
				this.goPage(0);
			}

			if (this.id) {
				const today = new Date();

				this.selectedFromDate = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0);
				this.selectedToDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

				this.queryParamSubscription = this.route.queryParams.subscribe(params => {
					this.selectedTaskHistoryId = params['taskHistoryId'];
					if (this.selectedTaskHistoryId) {
						this.taskHistoryService.get(this.selectedTaskHistoryId).subscribe((taskHistory: any) => {
							this.selectedTaskHistory = taskHistory;
							this.selectedIndex = 2;
						})
					}
				});
				this.doSearchHistory();
			}
		});
	}

	ngOnInit() {
		this.selectedIndex = this.loginService.getScreenConfig('scheduler-detail', 'tabIndex', 0);
	}

	ngOnDestroy() {
		if (this.routeSubscription) {
			this.routeSubscription.unsubscribe();
		}
		if (this.queryParamSubscription) {
			this.queryParamSubscription.unsubscribe();
		}
	}

	doSearchHistory() {
		if (this.oldSearch && this.oldSearch === this.searchTaskHistoryName) {
			return;
		} else {
			this.oldSearch = this.searchTaskHistoryName;

			this.doRefreshHistory(new DataReloadEvent('executedTime', 'desc', 0, 15, this.searchTaskHistoryName));
		}
	}

	goPage(pageNumber: number) {
		if (this.resultPage && pageNumber < 0) {
			return;
		} else if (this.resultPage && pageNumber > this.resultPage.totalElements / this.resultPage.size) {
			return;
		}

		this.ruleService.search('', 'name', 'asc', pageNumber, 10).subscribe((resultPage: any) => {
			this.rules = resultPage.content;
			this.rules.forEach((rule: any) => {
				if (rule.id === (this.selectedRule as any).id) {
					rule.viewSelected = true;
					this.selectedRule = rule;
				}
			});
			this.resultPage = resultPage;

			this.scheduler.rules.forEach(ruleMap => {
				for (const current of this.rules) {
					if (current.id === ruleMap.rule.id) {
						current.selected = true;
						break;
					}
				}
			});
		});
	}

	goBack() {
		this.router.navigate(['/main', { outlets: { content: 'service/scheduler/list' }}]);
	}

	doInitRule() {
		this.selectedRule = {
			name: '',
			description: '',
			viewSelected: false
		};
	}

	doViewRule(rule: any) {
		if (this.selectedRule) {
			this.selectedRule.viewSelected = false;
		}
		this.selectedRule = rule;
		this.selectedRule.viewSelected = true;
	}

	onRuleUpdated(rule: any) {
		this.selectedRule = rule;
		this.goPage(0);
	}

	doDelete() {
		this.translateService.get([
			'service.scheduler.detail.confirm.delete',
			'service.scheduler.detail.alert.deleted',
			'service.scheduler.detail.alert.delete-failed'
		]).subscribe((message: string) => {
			if (confirm(message['service.scheduler.detail.confirm.delete'])) {
				this.schedulerService.delete(this.id).subscribe(() => {
					alert(message['service.scheduler.detail.alert.deleted']);
					this.router.navigate(['/main', { outlets: { content: 'service/scheduler/list' }}]);
				}, () => {
					alert(message['service.scheduler.detail.alert.delete-failed']);
				});
			}
		});
	}

	doSave() {
		this.translateService.get([
			'common.invalid.alert',
			'service.scheduler.detail.confirm.save',
			'service.scheduler.detail.alert.saved',
			'service.scheduler.detail.alert.save-failed',
			'service.scheduler.detail.alert.missing-name'
		]).subscribe((messages: any) => {
			if (!this.scheduleForm.valid) {
				if (this.scheduleForm.controls) {
					for (const control in this.scheduleForm.controls) {
						this.scheduleForm.controls[control].markAsTouched();
					}
				}	
				alert(messages['common.invalid.alert']);
				return;
			} else {
				if (!this.scheduler.name || this.scheduler.name === '') {
					alert(messages['service.scheduler.detail.alert.missing-name']);
					return;
				}
		
				if (confirm(messages['service.scheduler.detail.confirm.save'])) {
					const ruleMaps = this.scheduler.rules;
		
					this.scheduler.rules = [];
					for (const rule of this.rules) {
						if (rule.selected) {
							let existing = false;
							for (const ruleMap of ruleMaps) {
								if (ruleMap.rule.id === rule.id) {
									this.scheduler.rules.push(ruleMap);
									existing = true;
									break;
								}
							}
			
							if (!existing) {
								this.scheduler.rules.push({
									rule: rule
								});
							}
						}
					}
					this.scheduler.rules = this.rules.filter((rule: any) => rule.selected).map(rule => {
						return {
							rule: rule
						}
					});
			
					this.schedulerService.save(this.id, this.scheduler).subscribe(() => {
						alert(messages['service.scheduler.detail.alert.saved']);
						this.router.navigate(['/main', { outlets: { content: 'service/scheduler/list' }}]);
					}, () => {
						alert(messages['service.scheduler.detail.alert.save-failed']);
					});	
				}
			}
		});
	}

	onTabChange() {
		this.loginService.setScreenConfig('scheduler-detail', 'tabIndex', this.selectedIndex);
	}

	doRefreshHistory(condition?: DataReloadEvent) {
		if (condition) {
			this.currentCondition = condition;
		} else {
			condition = this.currentCondition;
		}

		if (condition.pageNumber === 0) {
			this.loginService.setScreenConfig('scheduler.detail-task-history', 'sortItem', condition.active);
			this.loginService.setScreenConfig('scheduler.detail-task-history', 'sortOrder', condition.sortOrder);
		}

		if (!this.selectedFromDate.toUTCString) {
			const date = this.selectedFromDate as unknown as Moment;

			this.selectedFromDate = new Date(date.year(), date.month(), date.date(), 0, 0, 0);
		}
		if (!this.selectedToDate.toUTCString) {
			const date = this.selectedToDate as unknown as Moment;

			this.selectedToDate = new Date(date.year(), date.month(), date.date(), 23, 59, 59);
		}

		this.taskHistoryService.search(this.id, 
			this.selectedFromDate.toUTCString(), this.selectedToDate.toUTCString(),
			condition.filter, condition.active, condition.sortOrder, condition.pageNumber, condition.pageSize)
			.subscribe(resultPage => {
			this.taskHistoryList.refresh(resultPage.totalElements, resultPage.content, this.columns, condition);
		});
	}

	doShowHistory($event: any) {
		const history = $event.element.getData();

		this.selectedTaskHistoryId = history.id;
		this.selectedTaskHistory = history;
	}

	onTaskHistoryClose($event: any) {
		this.selectedTaskHistory = null;
		this.selectedTaskHistoryId = null;
	}
}
